/**
 * Matches - Match management page
 *
 * Features:
 * - Outlook-style calendar with color coding and tooltips
 * - Match cards styled like Overview's photo cards
 * - MatchHub integration for lookup + manual entry
 * - IPTC template editor (Photo Mechanic style)
 * - FTP server assignment
 * - Photographer/Editor assignments with sign-off
 * - Role-based views (COMPANY_MANAGER, PLANNER)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchMatches,
  fetchMatch,
  createMatch,
  updateMatch,
  deleteMatch,
  fetchTeams,
  createTeam,
  fetchUsers,
  assignPhotographerToMatch,
  unassignPhotographerFromMatch,
  fetchFtpTargetsForSelection,
  fetchCompanyIptcDefaults,
  fetchIptcPreview,
} from '../api';
import { usePermissions } from '../hooks';
import Spinner from '../shared/components/ui/Spinner';
import {
  Calendar,
  MatchCard,
  MatchModal,
  MatchDetails,
  FilterBar,
} from '../features/matches/components';
import { getMatchStatus, isMatchOnDate } from '../features/matches/utils/dateUtils';
import styles from './Matches.module.css';

/**
 * Default filter state
 */
const DEFAULT_FILTERS = {
  timeRange: 'week',
  status: 'all',
  assignment: 'all',
  eventTypes: [],
  sport: null,
  search: '',
};

export default function Matches() {
  // Permissions
  const { canManageMatches, role, currentUserId } = usePermissions();

  // Data state
  const [allMatches, setAllMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [editors, setEditors] = useState([]);
  const [ftpServers, setFtpServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarExpanded, setCalendarExpanded] = useState(false);

  // Filter state
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // Load initial data from API
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use capability-based filters instead of role-based
      // can_photograph: photographer, editor_photographer, company_manager
      // can_edit: editor, editor_photographer, company_manager
      // This allows the same user to appear in both lists (e.g., company_manager)
      const [matchesData, teamsData, photographersData, editorsData, ftpData] = await Promise.all([
        fetchMatches().catch(() => ({ results: [] })),
        fetchTeams().catch(() => ({ results: [] })),
        fetchUsers({ can_photograph: 'true' }).catch(() => ({ results: [] })),
        fetchUsers({ can_edit: 'true' }).catch(() => ({ results: [] })),
        fetchFtpTargetsForSelection().catch(() => ({ defaults: [], available: [] })),
      ]);

      setAllMatches(matchesData.results || matchesData || []);
      setTeams(teamsData.results || teamsData || []);
      setPhotographers(photographersData.results || photographersData || []);
      setEditors(editorsData.results || editorsData || []);
      // Combine defaults and available FTP targets into a flat list for MatchDetails
      const allFtpTargets = [...(ftpData.defaults || []), ...(ftpData.available || [])];
      setFtpServers(allFtpTargets);
    } catch (err) {
      setError('Failed to load data');
      setAllMatches([]);
      setTeams([]);
      setPhotographers([]);
      setEditors([]);
      setFtpServers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter matches
  const filteredMatches = useMemo(() => {
    let result = allMatches;

    // Filter by selected date from calendar
    if (selectedDate) {
      result = result.filter((m) => isMatchOnDate(m, selectedDate));
    }

    // Time range filter
    if (!selectedDate && filters.timeRange !== 'all') {
      const now = new Date();
      result = result.filter((match) => {
        const matchDate = new Date(match.start_time);
        if (filters.timeRange === 'today') {
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          return matchDate >= today && matchDate < tomorrow;
        } else if (filters.timeRange === 'week') {
          const weekStart = new Date(now);
          weekStart.setDate(now.getDate() - now.getDay());
          weekStart.setHours(0, 0, 0, 0);
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekEnd.getDate() + 7);
          return matchDate >= weekStart && matchDate < weekEnd;
        } else if (filters.timeRange === 'month') {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
          return matchDate >= monthStart && matchDate < monthEnd;
        }
        return true;
      });
    }

    // Status filter
    if (filters.status !== 'all') {
      result = result.filter((m) => getMatchStatus(m) === filters.status);
    }

    // Assignment filter
    if (filters.assignment === 'mine' && currentUserId) {
      result = result.filter((m) => {
        const isAssigned =
          m.photographer_assignments?.some((a) => a.user_id === currentUserId) ||
          m.editor_assignments?.some((a) => a.user_id === currentUserId);
        return isAssigned;
      });
    } else if (filters.assignment === 'unassigned') {
      result = result.filter((m) => {
        const hasAssignments =
          (m.photographer_assignments?.length || 0) > 0 ||
          (m.editor_assignments?.length || 0) > 0;
        return !hasAssignments;
      });
    }

    // Event type filter
    if (filters.eventTypes.length > 0) {
      result = result.filter((m) => filters.eventTypes.includes(m.event_type));
    }

    // Sport filter
    if (filters.sport) {
      result = result.filter((m) => m.sport === filters.sport);
    }

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter((m) => {
        const searchable = [
          m.name,
          m.home_team_name,
          m.away_team_name,
          m.venue,
          m.competition,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return searchable.includes(searchLower);
      });
    }

    // Sort: upcoming first, then live, then past
    result.sort((a, b) => {
      const aTime = new Date(a.start_time).getTime();
      const bTime = new Date(b.start_time).getTime();
      return aTime - bTime;
    });

    return result;
  }, [allMatches, selectedDate, filters, currentUserId]);

  // Get matches for a calendar date
  const getMatchesOnDate = useCallback(
    (date) => allMatches.filter((m) => isMatchOnDate(m, date)),
    [allMatches]
  );

  // Clear filters
  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSelectedDate(null);
  }, []);

  // Create/update match
  const handleSaveMatch = useCallback(
    async (matchData) => {
      try {
        let savedMatchId;
        if (editingMatch) {
          await updateMatch(editingMatch.id, matchData);
          savedMatchId = editingMatch.id;
        } else {
          const created = await createMatch(matchData);
          savedMatchId = created.id;
        }
        setShowMatchModal(false);
        setEditingMatch(null);
        loadData();

        // Refresh selected match details if we were editing it
        if (savedMatchId && selectedMatch?.id === savedMatchId) {
          const updated = await fetchMatch(savedMatchId);
          setSelectedMatch(updated);
        }
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
      }
    },
    [editingMatch, loadData, selectedMatch]
  );

  // Delete match
  const handleDeleteMatch = useCallback(async () => {
    if (!selectedMatch) return;
    if (!confirm('Delete this match?')) return;
    try {
      await deleteMatch(selectedMatch.id);
      setSelectedMatch(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, [selectedMatch, loadData]);

  // Edit match
  const handleEditMatch = useCallback(() => {
    if (!selectedMatch) return;
    setEditingMatch(selectedMatch);
    setShowMatchModal(true);
  }, [selectedMatch]);

  // Assign photographer
  const handleAssignPhotographer = useCallback(
    async (userId) => {
      if (!selectedMatch) return;
      try {
        await assignPhotographerToMatch(selectedMatch.id, userId);
        const updated = await fetchMatch(selectedMatch.id);
        setSelectedMatch(updated);
        loadData();
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    },
    [selectedMatch, loadData]
  );

  // Unassign photographer
  const handleUnassignPhotographer = useCallback(
    async (userId) => {
      if (!selectedMatch) return;
      try {
        await unassignPhotographerFromMatch(selectedMatch.id, userId);
        const updated = await fetchMatch(selectedMatch.id);
        setSelectedMatch(updated);
        loadData();
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    },
    [selectedMatch, loadData]
  );

  // Create team
  const handleCreateTeam = useCallback(
    async (name) => {
      try {
        const team = await createTeam(name, 'club');
        const teamsData = await fetchTeams();
        setTeams(teamsData.results || teamsData || []);
        return team;
      } catch (err) {
        setError(err.response?.data?.detail || err.message);
        return null;
      }
    },
    []
  );

  // Date selection from calendar
  const handleDateSelect = useCallback((date) => {
    setSelectedDate((prev) => {
      // Toggle off if same date
      if (
        prev &&
        prev.getFullYear() === date.getFullYear() &&
        prev.getMonth() === date.getMonth() &&
        prev.getDate() === date.getDate()
      ) {
        return null;
      }
      return date;
    });
  }, []);

  // Match click - fetch full details including IPTC config and FTP targets
  const handleMatchClick = useCallback(async (match) => {
    // Set the match immediately for responsive UI, then fetch full details
    setSelectedMatch(match);
    try {
      const fullMatch = await fetchMatch(match.id);
      setSelectedMatch(fullMatch);
    } catch (err) {
      console.error('Failed to load match details:', err);
      // Keep the basic match data if fetch fails
    }
  }, []);

  // Is a match live?
  const isLive = selectedMatch && getMatchStatus(selectedMatch) === 'live';

  if (loading && allMatches.length === 0) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <Spinner size="lg" />
          <p>Loading matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <h1 className={styles.title}>Matches</h1>
      </header>

      {/* Error banner */}
      {error && (
        <div className={styles.error}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Filter bar */}
      <FilterBar
        filters={filters}
        onChange={setFilters}
        onClear={handleClearFilters}
        onCreateMatch={() => {
          setEditingMatch(null);
          setShowMatchModal(true);
        }}
        canCreate={canManageMatches}
        role={role}
        matchCount={filteredMatches.length}
      />

      {/* Main layout */}
      <div className={styles.layout}>
        {/* Calendar sidebar */}
        <aside className={styles.sidebar}>
          <Calendar
            matches={allMatches}
            currentMonth={currentMonth}
            onMonthChange={setCurrentMonth}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            getMatchesOnDate={getMatchesOnDate}
            expanded={calendarExpanded}
            onToggleExpand={() => setCalendarExpanded(!calendarExpanded)}
          />
          {selectedDate && (
            <button className={styles.clearDate} onClick={() => setSelectedDate(null)}>
              Clear date filter
            </button>
          )}
        </aside>

        {/* Match list */}
        <main className={styles.main}>
          {selectedDate && (
            <p className={styles.dateLabel}>
              {selectedDate.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          )}

          {filteredMatches.length === 0 ? (
            <div className={styles.empty}>
              {allMatches.length === 0
                ? 'No matches yet. Create one to get started.'
                : 'No matches match the current filters.'}
            </div>
          ) : (
            <div className={styles.matchGrid}>
              {filteredMatches.map((match) => (
                <MatchCard
                  key={match.id}
                  match={match}
                  selected={selectedMatch?.id === match.id}
                  onClick={() => handleMatchClick(match)}
                />
              ))}
            </div>
          )}
        </main>

        {/* Details panel */}
        <aside className={styles.details}>
          <MatchDetails
            match={selectedMatch}
            photographers={photographers}
            editors={editors}
            ftpServers={ftpServers}
            onEdit={handleEditMatch}
            onDelete={handleDeleteMatch}
            onAssignPhotographer={handleAssignPhotographer}
            onUnassignPhotographer={handleUnassignPhotographer}
            canManage={canManageMatches}
            isLive={isLive}
          />
        </aside>
      </div>

      {/* Match Modal */}
      {showMatchModal && (
        <MatchModal
          isOpen={true}
          match={editingMatch}
          teams={teams}
          photographers={photographers}
          editors={editors}
          onSave={handleSaveMatch}
          onClose={() => {
            setShowMatchModal(false);
            setEditingMatch(null);
          }}
          onCreateTeam={handleCreateTeam}
          fetchFtpTargets={fetchFtpTargetsForSelection}
          fetchCompanyDefaults={fetchCompanyIptcDefaults}
          fetchIptcPreview={fetchIptcPreview}
        />
      )}
    </div>
  );
}
